<template>
  <b-card-code title="">
    <div class="d-flex align-items-center justify-content-between mb-2">
      <h4 class="mb-0">
        Km/ l kontroling
      </h4>
      <button
        class="btn btn-sm btn-outline-primary"
        :disabled="loading"
        @click="fetchData"
      >
        Frissítés
      </button>
    </div>

    <div class="row mb-2">
      <div class="col-lg-3">
        <label class="mb-50">Dátumtól</label>
        <input
          v-model="filters.from"
          type="date"
          class="form-control"
          :disabled="loading"
        >
      </div>
      <div class="col-lg-3">
        <label class="mb-50">Dátumig</label>
        <input
          v-model="filters.to"
          type="date"
          class="form-control"
          :disabled="loading"
        >
      </div>
      <div class="col-lg-3">
        <label class="mb-50">Rendszám</label>
        <select
          v-model="filters.licensePlate"
          class="form-control"
          :disabled="loading"
        >
          <option value="">
            Összes
          </option>
          <option
            v-for="plate in filterPlates"
            :key="`dashboard-plate-${plate}`"
            :value="plate"
          >
            {{ plate }}
          </option>
        </select>
      </div>
      <div class="col-lg-3 d-flex align-items-end">
        <button
          class="btn btn-primary mr-1"
          :disabled="loading"
          @click="fetchData"
        >
          Szűrés
        </button>
        <button
          class="btn btn-outline-secondary"
          :disabled="loading"
          @click="resetFilters"
        >
          Törlés
        </button>
      </div>
    </div>

    <div class="km-dashboard-wrap">
      <div class="km-card">
        <h5 class="mb-1">
          Figyelt járművek fogyasztása és napi futása
        </h5>

        <div
          v-if="loading"
          class="text-muted"
        >
          Betöltés...
        </div>

        <div
          v-else-if="!chartRows.length"
          class="text-muted"
        >
          Nincs megjeleníthető figyelt rendszám.
        </div>

        <div
          v-else
          class="km-chart-list"
        >
          <div class="km-group">
            <h6 class="km-group-title">
              SZ (Személygépkocsi)
            </h6>
            <div
              v-if="!chartRowsSz.length"
              class="text-muted small mb-1"
            >
              Nincs figyelt SZ jármű.
            </div>
            <div
              v-for="row in chartRowsSz"
              :key="`sz-${row.license_plate}`"
              class="km-plate-card"
            >
              <div class="km-chart-row">
                <div class="km-plate">
                  {{ row.license_plate }}
                </div>

                <div class="km-metrics">
                  <div class="km-metric">
                    <div class="km-metric-head">
                      <span>Fogyasztás (l/100km)</span>
                      <span>{{ formatNumber(row.avg_l_100km) }}</span>
                    </div>
                    <div class="km-bar-track">
                      <div
                        class="km-bar-fill"
                        :class="fuelClass(row.avg_l_100km)"
                        :style="{ width: `${metricPercent(row.avg_l_100km, 15)}%` }"
                      />
                    </div>
                  </div>

                  <div class="km-metric">
                    <div class="km-metric-head">
                      <span>Napi futás (km/nap)</span>
                      <span>{{ formatNumber(row.avg_km_day) }}</span>
                    </div>
                    <div class="km-bar-track">
                      <div
                        class="km-bar-fill km-bar-km"
                        :style="{ width: `${metricPercent(row.avg_km_day, maxDailyKm)}%` }"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="km-group mt-2">
            <h6 class="km-group-title">
              TGK (Tehergépkocsi)
            </h6>
            <div
              v-if="!chartRowsTgk.length"
              class="text-muted small mb-1"
            >
              Nincs figyelt TGK jármű.
            </div>
            <div
              v-for="row in chartRowsTgk"
              :key="`tgk-${row.license_plate}`"
              class="km-plate-card"
            >
              <div class="km-chart-row">
                <div class="km-plate">
                  {{ row.license_plate }}
                </div>

                <div class="km-metrics">
                  <div class="km-metric">
                    <div class="km-metric-head">
                      <span>Fogyasztás (l/100km)</span>
                      <span>{{ formatNumber(row.avg_l_100km) }}</span>
                    </div>
                    <div class="km-bar-track">
                      <div
                        class="km-bar-fill"
                        :class="fuelClass(row.avg_l_100km)"
                        :style="{ width: `${metricPercent(row.avg_l_100km, 15)}%` }"
                      />
                    </div>
                  </div>

                  <div class="km-metric">
                    <div class="km-metric-head">
                      <span>Napi futás (km/nap)</span>
                      <span>{{ formatNumber(row.avg_km_day) }}</span>
                    </div>
                    <div class="km-bar-track">
                      <div
                        class="km-bar-fill km-bar-km"
                        :style="{ width: `${metricPercent(row.avg_km_day, maxDailyKm)}%` }"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-2">
              <h6 class="km-group-title mb-1">
                TGK autócsere javaslat (kiegyenlített kihasználtság)
              </h6>
              <div
                v-if="!tgkSwapSuggestions.length"
                class="text-muted small"
              >
                Nincs elég valid TGK adat cserejavaslat készítéséhez.
              </div>
              <div
                v-else
                class="table-responsive"
              >
                <table class="table table-sm table-striped mb-0 km-swap-table">
                  <thead>
                    <tr>
                      <th>Prioritás</th>
                      <th>Kímélendő autó</th>
                      <th>Terhelendő autó</th>
                      <th>Napi futás csere (km/nap)</th>
                      <th>Km óra különbség</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(swap, idx) in tgkSwapSuggestions"
                      :key="`swap-${swap.high_plate}-${swap.low_plate}`"
                    >
                      <td>{{ idx + 1 }}</td>
                      <td>
                        {{ swap.high_plate }}
                        ({{ formatNumber(swap.high_km_day) }} km/nap, {{ formatInteger(swap.high_odometer) }} km)
                      </td>
                      <td>
                        {{ swap.low_plate }}
                        ({{ formatNumber(swap.low_km_day) }} km/nap, {{ formatInteger(swap.low_odometer) }} km)
                      </td>
                      <td>{{ formatNumber(swap.daily_km_shift) }}</td>
                      <td>{{ formatInteger(swap.odometer_gap) }} km</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </b-card-code>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  data() {
    return {
      loading: false,
      chartRows: [],
      chartRowsSz: [],
      chartRowsTgk: [],
      tgkSwapSuggestions: [],
      maxDailyKm: 0,
      filters: {
        from: '',
        to: '',
        licensePlate: '',
      },
      filterPlates: [],
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const response = await axios({
          method: 'get',
          url: 'km-l-controlling/list',
          params: {
            from: this.filters.from || undefined,
            to: this.filters.to || undefined,
            license_plate: this.filters.licensePlate || undefined,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        })

        const summary = Array.isArray(response?.data?.summary) ? response.data.summary : []
        const trackedPlates = Array.isArray(response?.data?.tracked_plates) ? response.data.tracked_plates : []
        const trackedOnly = trackedPlates.filter(item => item && item.is_tracked)
        this.filterPlates = trackedOnly.map(item => item.license_plate).sort()
        const summaryMap = summary.reduce((acc, row) => {
          acc[row.license_plate] = row
          return acc
        }, {})

        this.chartRows = trackedOnly.map(item => {
          const summaryRow = summaryMap[item.license_plate] || {}
          return {
            license_plate: item.license_plate,
            vehicle_category: item.vehicle_category || 'SZ',
            avg_l_100km: summaryRow.avg_l_100km ?? null,
            avg_km_day: summaryRow.avg_km_day ?? null,
            last_valid_odometer_km: summaryRow.last_valid_odometer_km ?? null,
          }
        })

        this.chartRowsSz = this.chartRows.filter(item => item.vehicle_category === 'SZ')
        this.chartRowsTgk = this.chartRows.filter(item => item.vehicle_category === 'TGK')
        this.tgkSwapSuggestions = this.buildTgkSwapSuggestions(this.chartRowsTgk)
        this.maxDailyKm = this.findMax(this.chartRows.map(row => row.avg_km_day))
      } catch (error) {
        const message = error?.response?.data?.message || 'Kimutatás betöltése sikertelen'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: message,
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },
    resetFilters() {
      this.filters.from = ''
      this.filters.to = ''
      this.filters.licensePlate = ''
      this.fetchData()
    },
    findMax(values) {
      const valid = values
        .map(value => Number(value))
        .filter(value => !Number.isNaN(value) && value > 0)
      return valid.length ? Math.max(...valid) : 0
    },
    metricPercent(value, max) {
      const num = Number(value)
      if (Number.isNaN(num) || num <= 0 || max <= 0) {
        return 0
      }
      const percent = (num / max) * 100
      return Math.max(3, Math.min(100, percent))
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      const num = Number(value)
      if (Number.isNaN(num)) {
        return '-'
      }
      return num.toLocaleString('hu-HU', { maximumFractionDigits: 2 })
    },
    formatInteger(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      const num = Number(value)
      if (Number.isNaN(num)) {
        return '-'
      }
      return Math.round(num).toLocaleString('hu-HU')
    },
    fuelClass(value) {
      const num = Number(value)
      if (Number.isNaN(num) || num <= 0) {
        return 'km-bar-fuel-green'
      }
      if (num <= 10) {
        return 'km-bar-fuel-green'
      }
      if (num <= 11.5) {
        return 'km-bar-fuel-yellow'
      }
      return 'km-bar-fuel-red'
    },
    buildTgkSwapSuggestions(rows) {
      const tgk = rows
        .map(row => ({
          license_plate: row.license_plate,
          avg_km_day: Number(row.avg_km_day),
          odometer_km: Number(row.last_valid_odometer_km),
        }))
        .filter(row => !Number.isNaN(row.avg_km_day) && row.avg_km_day > 0
          && !Number.isNaN(row.odometer_km) && row.odometer_km > 0)

      if (tgk.length < 2) {
        return []
      }

      const highLoad = [...tgk].sort((a, b) => b.avg_km_day - a.avg_km_day)
      const lowLoad = [...tgk].sort((a, b) => a.avg_km_day - b.avg_km_day)
      const usedPlates = new Set()
      const suggestions = []

      highLoad.forEach(heavy => {
        if (usedPlates.has(heavy.license_plate)) {
          return
        }

        let bestMatch = null
        let bestScore = 0

        lowLoad.forEach(light => {
          if (heavy.license_plate === light.license_plate || usedPlates.has(light.license_plate)) {
            return
          }

          const odometerGap = heavy.odometer_km - light.odometer_km
          const dailyKmShift = heavy.avg_km_day - light.avg_km_day

          if (odometerGap <= 0 || dailyKmShift <= 0) {
            return
          }

          // Apró eltéréseket ne javasoljon.
          if (odometerGap < 3000 || dailyKmShift < 10) {
            return
          }

          const score = odometerGap * dailyKmShift
          if (score > bestScore) {
            bestScore = score
            bestMatch = {
              high_plate: heavy.license_plate,
              high_km_day: heavy.avg_km_day,
              high_odometer: heavy.odometer_km,
              low_plate: light.license_plate,
              low_km_day: light.avg_km_day,
              low_odometer: light.odometer_km,
              odometer_gap: odometerGap,
              daily_km_shift: dailyKmShift,
              priority_score: score,
            }
          }
        })

        if (bestMatch) {
          usedPlates.add(bestMatch.high_plate)
          usedPlates.add(bestMatch.low_plate)
          suggestions.push(bestMatch)
        }
      })

      return suggestions.sort((a, b) => b.priority_score - a.priority_score)
    },
  },
}
</script>

<style scoped>
.km-card {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px;
  width: 100%;
  max-width: 50%;
}

.km-dashboard-wrap {
  display: flex;
  align-items: flex-start;
}

.km-chart-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.km-group-title {
  font-size: 0.95rem;
  margin-bottom: 8px;
  opacity: 0.9;
}

.km-chart-row {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 12px;
  align-items: center;
}

.km-plate-card {
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 10px 10px 8px;
  background: rgba(255, 255, 255, 0.02);
  margin-bottom: 8px;
}

.km-plate {
  font-weight: 600;
  letter-spacing: 0.4px;
}

.km-metrics {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.km-metric-head {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
}

.km-bar-track {
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  overflow: hidden;
}

.km-bar-fill {
  height: 100%;
  border-radius: 999px;
}

.km-bar-fuel-green {
  background: #22c55e;
}

.km-bar-fuel-yellow {
  background: #facc15;
}

.km-bar-fuel-red {
  background: #ef4444;
}

.km-bar-km {
  background: linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%);
}

.km-swap-table th,
.km-swap-table td {
  white-space: nowrap;
}

@media (max-width: 1200px) {
  .km-card {
    max-width: 100%;
  }
}
</style>
